<template>
  <v-card outlined class="d-flex flex-column fill-height" elevation="0">
    <!-- Header -->
    <v-card-title class="d-flex align-center py-3 px-4" style="height: 64px">
      <div class="text-subtitle-1 text--secondary">Price Index Distribution</div>
      <v-spacer></v-spacer>
      <v-btn-toggle v-model="viewMode" mandatory dense color="primary" class="mr-2">
        <v-btn value="chart" small>
          <v-icon small left>mdi-chart-bar</v-icon>
        </v-btn>
        <v-btn value="list" small>
          <v-icon small left>mdi-format-list-bulleted</v-icon>
        </v-btn>
      </v-btn-toggle>
      <v-btn icon small @click="fetchData">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>
    <v-divider></v-divider>

    <!-- Summary Section -->
    <div
      class="px-4 py-3 lighten-5 d-flex align-center justify-space-between"
      style="height: 80px"
      v-if="!loading && hasData"
    >
      <div>
        <span class="text-h5 font-weight-bold primary--text">{{ total }}</span>
        <span class="text-caption text--secondary ml-1">products analyzed</span>
      </div>
      <div class="text-right">
        <div class="text-subtitle-2 font-weight-bold success--text">{{ samePricePercentage }}%</div>
        <div class="text-caption text--secondary">({{ equal_than_average }}) same price</div>
      </div>
    </div>

    <!-- <v-divider v-if="!loading && hasData"></v-divider> -->

    <!-- Content Area -->
    <v-card-text
      class="flex-grow-1 d-flex flex-column justify-center relative pa-4"
      style="min-height: 250px"
    >
      <!-- Loading State -->
      <div v-if="loading" class="d-flex flex-column align-center justify-center fill-height">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        <div class="mt-3 text-caption text--secondary">Loading price distribution data...</div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!hasData" class="text-center my-auto">
        <v-icon size="64" color="grey lighten-2" class="mb-3">mdi-chart-bar</v-icon>
        <div class="text-subtitle-1 text--secondary font-weight-medium">No Data Available</div>
        <div class="text-caption text--secondary mt-1">
          Add matched products to see price distribution
        </div>
      </div>

      <!-- Chart View -->
      <div
        v-else-if="viewMode === 'chart'"
        class="d-flex flex-column align-center justify-center fill-height w-100"
      >
        <div style="position: relative; height: 200px; width: 100%">
          <canvas ref="distributionChart" id="distributionChart"></canvas>
        </div>
      </div>

      <!-- List View -->
      <div v-else-if="viewMode === 'list'" class="fill-height overflow-y-auto w-100">
        <v-row no-gutters>
          <v-col cols="6" class="pr-2" style="border-right: 1px solid #e0e0e0">
            <div class="text-caption text--secondary mb-2 text-center">Cheaper</div>
            <v-list dense class="pa-0">
              <v-list-item
                v-for="(item, i) in lowerHalfLabels"
                :key="`lower-${i}`"
                @click="!disable_filter && filterPriceIndex(item.label)"
                class="px-2 rounded mb-1"
                :class="{ 'v-list-item--active': selectedPriceIndex === item.label }"
                :ripple="!disable_filter"
                :style="disable_filter ? 'cursor: default' : ''"
              >
                <v-list-item-icon class="mr-2 my-auto">
                  <v-icon :color="item.color" x-small>mdi-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-caption">
                    &lt; {{ item.label }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action
                  class="my-auto d-flex flex-row align-center justify-start"
                  style="min-width: 80px"
                >
                  <span class="text-caption font-weight-bold mr-2">{{ item.count }}</span>
                  <div
                    :style="{
                      width: (item.count / maxCount) * 50 + 'px',
                      height: '6px',
                      backgroundColor: item.color,
                      borderRadius: '2px',
                    }"
                  ></div>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col cols="6" class="pl-2">
            <div class="text-caption text--secondary mb-2 text-center">More Expensive</div>
            <v-list dense class="pa-0">
              <v-list-item
                v-for="(item, i) in upperHalfLabels"
                :key="`upper-${i}`"
                @click="!disable_filter && filterPriceIndex(item.label)"
                class="px-2 rounded mb-1"
                :class="{ 'v-list-item--active': selectedPriceIndex === item.label }"
                :ripple="!disable_filter"
                :style="disable_filter ? 'cursor: default' : ''"
              >
                <v-list-item-icon class="mr-2 my-auto">
                  <v-icon :color="item.color" x-small>mdi-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-caption">
                    &gt; {{ item.label }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action
                  class="my-auto d-flex flex-row align-center justify-start"
                  style="min-width: 80px"
                >
                  <span class="text-caption font-weight-bold mr-2">{{ item.count }}</span>
                  <div
                    :style="{
                      width: (item.count / maxCount) * 50 + 'px',
                      height: '6px',
                      backgroundColor: item.color,
                      borderRadius: '2px',
                    }"
                  ></div>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </div>
    </v-card-text>

    <v-divider v-if="!loading && hasData"></v-divider>

    <!-- Footer -->
    <v-card-actions class="px-4 py-2 lighten-5" v-if="!loading && hasData && !disable_filter">
      <v-chip
        v-if="selectedPriceIndex"
        close
        small
        color="secondary"
        @click:close="clearPriceIndexFilter"
        class="rounded-sm"
      >
        <!-- <v-icon left small>mdi-filter</v-icon> -->
        {{ selectedPriceIndex }}
      </v-chip>
      <v-spacer></v-spacer>
      <div class="text-caption text--secondary d-flex align-center">
        Updated {{ formattedLastUpdate }}
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
  import Chart from 'chart.js'
  import ChartDataLabels from 'chartjs-plugin-datalabels'
  import axios from 'axios'

  // Unregister globally if that's the intended behavior from original code
  Chart.plugins.unregister(ChartDataLabels)

  // Extend Chart.js Rectangle to support rounded corners
  const originalDraw = Chart.elements.Rectangle.prototype.draw
  Chart.elements.Rectangle.prototype.draw = function () {
    const vm = this._view
    // Check if dataset exists and has cornerRadius
    const dataset = this._chart.data.datasets[this._datasetIndex]

    if (dataset && dataset.cornerRadius) {
      const ctx = this._chart.ctx
      const left = vm.x - vm.width / 2
      const right = vm.x + vm.width / 2
      const top = vm.y
      const bottom = vm.base
      // Ensure radius doesn't exceed dimensions
      // Use Math.abs for height in case of negative values or coordinate system differences
      const height = Math.abs(bottom - top)
      const width = Math.abs(right - left)
      const radius = Math.min(dataset.cornerRadius, width / 2, height / 2)

      ctx.beginPath()
      ctx.fillStyle = vm.backgroundColor
      ctx.strokeStyle = vm.borderColor

      // Draw rounded top
      ctx.moveTo(left, bottom)
      ctx.lineTo(left, top + radius)
      ctx.quadraticCurveTo(left, top, left + radius, top)
      ctx.lineTo(right - radius, top)
      ctx.quadraticCurveTo(right, top, right, top + radius)
      ctx.lineTo(right, bottom)
      ctx.closePath()

      ctx.fill()

      // Draw border
      if (vm.borderWidth) {
        if (typeof vm.borderWidth === 'object' && vm.borderWidth.top) {
          ctx.lineWidth = vm.borderWidth.top
          ctx.beginPath()
          ctx.moveTo(left, top + radius)
          ctx.quadraticCurveTo(left, top, left + radius, top)
          ctx.lineTo(right - radius, top)
          ctx.quadraticCurveTo(right, top, right, top + radius)
          ctx.stroke()
        } else if (typeof vm.borderWidth === 'number') {
          ctx.lineWidth = vm.borderWidth
          // Redraw path without closing it to avoid bottom border
          ctx.beginPath()
          ctx.moveTo(left, bottom)
          ctx.lineTo(left, top + radius)
          ctx.quadraticCurveTo(left, top, left + radius, top)
          ctx.lineTo(right - radius, top)
          ctx.quadraticCurveTo(right, top, right, top + radius)
          ctx.lineTo(right, bottom)
          ctx.stroke()
        }
      }
    } else {
      originalDraw.apply(this, arguments)
    }
  }

  export default {
    name: 'ChartCardDistribution',
    props: {
      component_name: {
        type: String,
        default: '',
      },
      filter_reset: {
        type: Boolean,
        default: false,
      },
      disable_filter: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        loading: false,
        viewMode: 'chart', // 'chart' or 'list'
        chartInstance: null,

        // Data storage
        statistics: {
          data: [],
          labels: ['-50%', '-20%', '-10%', '-5%', '-1% ~ 1%', '5%', '10%', '20%', '50%'],
          colors: [
            '#FF5252',
            '#FF9800',
            '#FFC107',
            '#4CAF50',
            '#4CAF50', // Middle one (equal)
            '#4CAF50',
            '#FFC107',
            '#FF9800',
            '#FF5252',
          ],
        },

        // Summary stats
        higher_than_average: 0,
        lower_than_average: 0,
        equal_than_average: 0,
        total: 0,
        lastUpdateDate: '',

        selectedPriceIndex: null,
      }
    },
    computed: {
      hasData() {
        return this.total > 0
      },
      samePricePercentage() {
        if (!this.total) return '0.0'
        return ((this.equal_than_average / this.total) * 100).toFixed(1)
      },
      formattedLastUpdate() {
        if (!this.lastUpdateDate) return ''
        return this.dateToDayName(this.lastUpdateDate)
      },
      // Helper for list view
      processedListData() {
        // Combine labels, data, and colors into a single array of objects
        return this.statistics.labels.map((label, index) => ({
          label,
          count: this.statistics.data[index] || 0,
          color: this.statistics.colors[index],
        }))
      },
      lowerHalfLabels() {
        const mid = Math.floor(this.processedListData.length / 2)
        return this.processedListData.slice(0, mid)
      },
      upperHalfLabels() {
        const mid = Math.floor(this.processedListData.length / 2)
        // Skip the middle one (equal) if it's odd length, or adjust logic
        // Original code: slice(length/2 + 1, length)
        return this.processedListData.slice(mid + 1)
      },
      maxCount() {
        if (!this.statistics.data || this.statistics.data.length === 0) return 1
        return Math.max(...this.statistics.data) || 1
      },
    },
    watch: {
      filter_reset() {
        this.selectedPriceIndex = null
      },
      viewMode(val) {
        localStorage.setItem('distrubutionChartMode', val === 'chart')
        if (val === 'chart' && this.hasData) {
          this.$nextTick(() => {
            this.renderChart()
          })
        }
      },
    },
    mounted() {
      this.update()
    },
    methods: {
      update() {
        const savedMode = localStorage.getItem('distrubutionChartMode')
        if (savedMode !== null) {
          this.viewMode = savedMode === 'true' ? 'chart' : 'list'
        }
        this.fetchData()
      },
      dateToDayName(date) {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        const dateParts = date.split('-')
        const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])

        const today = new Date()
        const yesterday = new Date(new Date().setDate(new Date().getDate() - 1))

        if (dateObj.toDateString() === today.toDateString()) return 'Today'
        if (dateObj.toDateString() === yesterday.toDateString()) return 'Yesterday'
        return days[dateObj.getDay()]
      },

      async fetchData() {
        this.loading = true
        try {
          const res = await axios.get(`/api/products/statisticslaststat`, {
            params: {
              account: localStorage.getItem('account_id'),
              user: localStorage.getItem('user_id'),
              updateInterval: localStorage.getItem('updateInterval') || 1,
              match: 1,
              component: this.component_name,
              search_filter: localStorage.getItem('filter'),
            },
          })

          this.lower_than_average = res.data.resume.lower_than_average
          this.higher_than_average = res.data.resume.higher_than_average
          this.equal_than_average = res.data.resume.equal_than_average
          this.total = res.data.resume.total
          this.lastUpdateDate = res.data.resume.lastUpdateDate
          this.processStatisticsData(res.data.statisticsData)
        } catch (error) {
          console.error('Error fetching statistics', error)
        } finally {
          this.loading = false
          if (this.viewMode === 'chart' && this.hasData) {
            this.$nextTick(() => this.renderChart())
          }
        }
      },

      processStatisticsData(data) {
        // Initialize data array with zeros
        const newData = new Array(this.statistics.labels.length).fill(0)

        data.forEach((datum) => {
          const key = datum.key
          let labelToMatch = ''
          if (key === 0) {
            labelToMatch = '-1% ~ 1%'
          } else {
            labelToMatch = key + '%'
          }

          const index = this.statistics.labels.indexOf(labelToMatch)
          if (index !== -1) {
            newData[index] = datum.doc_count
          }
        })

        this.statistics.data = newData
      },

      renderChart() {
        if (this.chartInstance) {
          this.chartInstance.destroy()
        }

        const canvas = this.$refs.distributionChart
        if (!canvas) return
        const ctx = canvas.getContext('2d')

        // Helper to convert hex to rgba
        const hexToRgba = (hex, alpha) => {
          const r = parseInt(hex.slice(1, 3), 16)
          const g = parseInt(hex.slice(3, 5), 16)
          const b = parseInt(hex.slice(5, 7), 16)
          return `rgba(${r}, ${g}, ${b}, ${alpha})`
        }

        const backgroundColors = this.statistics.colors.map((color) => {
          const gradient = ctx.createLinearGradient(0, 0, 0, 200)
          gradient.addColorStop(0, hexToRgba(color, 0.5))
          gradient.addColorStop(1, hexToRgba(color, 0.05))
          return gradient
        })

        this.chartInstance = new Chart(canvas, {
          type: 'bar',
          plugins: [ChartDataLabels],
          data: {
            labels: this.statistics.labels,
            datasets: [
              {
                data: this.statistics.data,
                backgroundColor: backgroundColors,
                borderColor: this.statistics.colors,
                borderWidth: 1,
                borderSkipped: false,
                cornerRadius: 6,
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            legend: { display: false },
            responsive: true,
            layout: { padding: { top: 25 } },
            scales: {
              xAxes: [
                {
                  gridLines: { display: false, drawBorder: false },
                  ticks: { fontSize: 10 },
                },
              ],
              yAxes: [
                {
                  display: false,
                  ticks: { beginAtZero: true },
                },
              ],
            },
            tooltips: {
              callbacks: {
                label: (tooltipItem, data) => {
                  const value = data.datasets[0].data[tooltipItem.index]
                  return `${value} products`
                },
              },
            },
            onClick: (event, elements) => {
              if (this.disable_filter) return
              if (elements.length > 0) {
                const index = elements[0]._index
                this.selectPriceIndex(this.statistics.labels[index])
              }
            },
            hover: {
              onHover: (e, elements) => {
                if (this.disable_filter) {
                  e.target.style.cursor = 'default'
                  return
                }
                e.target.style.cursor = elements.length ? 'pointer' : 'default'
              },
            },
            plugins: {
              datalabels: {
                align: 'end',
                anchor: 'end',
                font: { size: 11, weight: 600 },
                offset: 4,
                color: '#9e9e9e',
                formatter: (value) => (value > 0 ? value : ''),
              },
            },
          },
        })
      },

      filterPriceIndex(label) {
        this.selectPriceIndex(label)
      },

      selectPriceIndex(label) {
        this.selectedPriceIndex = label
        // Convert label to value expected by parent
        // '-1% ~ 1%' -> 0
        // '-50%' -> -50
        let value = 0
        if (label !== '-1% ~ 1%') {
          value = parseInt(label.replace('%', ''))
        }

        this.$emit('onSelectPriceIndex', value)
      },

      clearPriceIndexFilter() {
        this.selectedPriceIndex = null
        this.$emit('onSelectPriceIndex', null)
      },
    },
  }
</script>

<style scoped>
  .v-list-item--active {
    background-color: #f5f5f5;
    border: 1px solid #e0e0e0;
  }
</style>
